import { zodResolver } from "@hookform/resolvers/zod";
import { PencilIcon, PlusIcon, TrashIcon } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

import { create as createSchema } from "@blis/shared/schemas/test-spec-group";
import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { AgGridReact } from "@/components/grid/AgGrid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateHeading,
} from "@/components/ui/empty-state";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { ItemDetail } from "@/components/ui/list-box";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { TestSpecGroup } from "../../types";
import { usePersistedAdminGridOptions } from "../../utils/gridState";
import { TableFilterRow } from "../TableFilterRow";

export function TestSpecGroupTable() {
  const { data } = trpc.admin.testSpecGroup.list.useQuery();

  const { ref, options, filterTextProps } = usePersistedAdminGridOptions(
    "test-spec-groups",
    data,
    [
      { field: "name", headerName: "Group" },
      { field: "description", headerName: "Description" },
      {
        headerName: "Tests",
        cellRenderer: (params) => <Text>{params.value}</Text>,
        cellRendererParams: (params) => ({
          value: params.data.testSpecs.length,
        }),
      },
      {
        headerName: "Actions",
        cellRenderer: (params) => (
          <>
            <AddTestSpecGroupDialog group={params.data}>
              <Button size="icon-sm" variant="ghost" className="mr-1">
                <PencilIcon data-slot="icon" />
              </Button>
            </AddTestSpecGroupDialog>
            <DeleteTestSpecGroupDialog group={params.data} />
          </>
        ),
        cellRendererParams: (params) => ({ data: params.data }),
      },
    ],
    {
      noRowsOverlayComponent: () => (
        <EmptyState>
          <EmptyStateHeading>No test spec groups</EmptyStateHeading>
          <EmptyStateDescription>
            No test spec groups have been created yet.
          </EmptyStateDescription>
        </EmptyState>
      ),
    },
  );

  return (
    <>
      <TableFilterRow inputProps={filterTextProps}>
        <AddTestSpecGroupDialog>
          <Button>
            <PlusIcon data-slot="icon" /> Add group
          </Button>
        </AddTestSpecGroupDialog>
      </TableFilterRow>
      <div className="ag-theme-quartz ag-theme-blis flex-1">
        <AgGridReact {...options} ref={ref} />
      </div>
    </>
  );
}

type FormValues = z.infer<typeof createSchema>;

function AddTestSpecGroupDialog({
  group,
  children,
}: {
  group?: TestSpecGroup;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  const testSpecs = trpc.admin.testSpec.list.useQuery();

  const create = trpc.admin.testSpecGroup.create.useMutation();
  const update = trpc.admin.testSpecGroup.update.useMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...group,
      testSpecIds: group?.testSpecs.map((testSpec) => testSpec.id) || [],
    },
    resolver: zodResolver(createSchema),
  });

  useEffect(() => {
    if (open) {
      reset({
        ...group,
        testSpecIds: group?.testSpecs.map((testSpec) => testSpec.id) || [],
      });
    }
  }, [reset, open, group]);

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (group) {
      update.mutate(
        { id: group.id, ...values },
        mutationResponse({
          errorTitle: "Failed to update test spec group",
          successMessage: "Test spec group updated",
          onSuccess() {
            setOpen(false);
          },
        }),
      );
    } else {
      create.mutate(
        values,
        mutationResponse({
          errorTitle: "Failed to create test spec group",
          successMessage: "Test spec group created",
          onSuccess() {
            setOpen(false);
          },
        }),
      );
    }
  };

  const disabledKeys = useMemo(() => {
    return testSpecs.data
      ?.filter((t) => t.group && t.group.id !== group?.id)
      .map((t) => t.id);
  }, [group, testSpecs.data]);

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      {children}
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>
              {group ? "Edit" : "Create"} test spec group
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              onSubmit={handleSubmit(onSubmit)}
              id="create-test-group-form"
              className="flex flex-col gap-3"
            >
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField {...field}>
                    <Label>Name</Label>
                    <Input />
                    <FieldError>{errors.name?.message}</FieldError>
                  </TextField>
                )}
              />
              <Controller
                name="description"
                control={control}
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField {...field} value={field.value || ""}>
                    <Label>Description</Label>
                    <Input />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />

              <Controller
                name="testSpecIds"
                control={control}
                render={({ field }) => (
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Test specs</Label>
                      <Text size="1" muted>
                        {field.value?.length || 0} selected
                      </Text>
                    </div>
                    <GridList
                      items={testSpecs.data || []}
                      aria-label="Test specs"
                      selectionMode="multiple"
                      // isLoading={testSpecs.isPending}
                      disabledKeys={disabledKeys}
                      selectedKeys={field.value?.map((id) => id)}
                      onSelectionChange={(keys) => {
                        const ids =
                          keys === "all"
                            ? testSpecs.data
                                ?.filter((t) => !disabledKeys?.includes(t.id))
                                .map((t) => t.id)
                            : Array.from(keys).map(Number);

                        field.onChange(ids || []);
                      }}
                    >
                      {(testSpec) => (
                        <GridListItem
                          key={testSpec.id}
                          textValue={`${testSpec.name} (${testSpec.code})`}
                        >
                          <div className="flex w-full min-w-0 items-center justify-between gap-2">
                            <div className="flex min-w-0 items-center gap-2">
                              <Text className="truncate">{testSpec.name}</Text>
                              {testSpec.group && (
                                <Badge size="sm">{testSpec.group.name}</Badge>
                              )}
                            </div>
                            <ItemDetail>{testSpec.code}</ItemDetail>
                          </div>
                        </GridListItem>
                      )}
                    </GridList>
                  </div>
                )}
              />
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              form="create-test-group-form"
              type="submit"
              isPending={create.isPending}
            >
              Submit
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}

function DeleteTestSpecGroupDialog({ group }: { group: { id: number } }) {
  const [open, setOpen] = useState(false);
  const deleteGroup = trpc.admin.testSpecGroup.delete.useMutation();

  const onConfirm = () => {
    deleteGroup.mutate(
      { id: group.id },
      mutationResponse({
        errorTitle: "Failed to delete test spec group",
        successMessage: "Test spec group deleted",
        onSuccess() {
          setOpen(false);
        },
      }),
    );
  };

  return (
    <ConfirmPrompt
      danger
      open={open}
      onOpenChange={setOpen}
      title="Delete test spec group"
      message="Are you sure you want to delete this test spec group?"
      onConfirm={onConfirm}
    >
      <Button size="icon-sm" variant="ghost" className="text-destructive">
        <TrashIcon />
      </Button>
    </ConfirmPrompt>
  );
}
